import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Grid,
  Fab
} from '@mui/material';
import {
  Add,
  Delete,
  Search,
  QrCodeScanner
} from '@mui/icons-material';
import { productsAPI } from '../services/api';
import { Product, ProductCreate, NutritionalDeclaration } from '../types';

const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [newProduct, setNewProduct] = useState<ProductCreate>({
    name: '',
    description: '',
    barcode: '',
    ingredients: [],
    nutritional_declaration: {
      calories: undefined,
      protein: undefined,
      carbohydrates: undefined,
      fat: undefined,
      fiber: undefined,
      sugar: undefined,
      sodium: undefined,
      saturated_fat: undefined,
      trans_fat: undefined,
      cholesterol: undefined
    }
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productsAPI.getAll(searchTerm || undefined);
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
      showSnackbar('Error loading products', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = () => {
    setNewProduct({
      name: '',
      description: '',
      barcode: '',
      ingredients: [],
      nutritional_declaration: {
        calories: undefined,
        protein: undefined,
        carbohydrates: undefined,
        fat: undefined,
        fiber: undefined,
        sugar: undefined,
        sodium: undefined,
        saturated_fat: undefined,
        trans_fat: undefined,
        cholesterol: undefined
      }
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSaveProduct = async () => {
    try {
      await productsAPI.create(newProduct);
      showSnackbar('Product created successfully', 'success');
      handleCloseDialog();
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      showSnackbar('Error saving product', 'error');
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productsAPI.delete(productId);
        showSnackbar('Product deleted successfully', 'success');
        loadProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        showSnackbar('Error deleting product', 'error');
      }
    }
  };

  const handleIngredientChange = (index: number, value: string) => {
    const newIngredients = [...newProduct.ingredients];
    newIngredients[index] = value;
    setNewProduct({ ...newProduct, ingredients: newIngredients });
  };

  const addIngredient = () => {
    setNewProduct({ ...newProduct, ingredients: [...newProduct.ingredients, ''] });
  };

  const removeIngredient = (index: number) => {
    const newIngredients = newProduct.ingredients.filter((_, i) => i !== index);
    setNewProduct({ ...newProduct, ingredients: newIngredients });
  };

  const handleNutritionalChange = (field: keyof NutritionalDeclaration, value: string) => {
    const numValue = value === '' ? undefined : parseFloat(value);
    setNewProduct({
      ...newProduct,
      nutritional_declaration: {
        ...newProduct.nutritional_declaration,
        [field]: numValue
      }
    });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Product Management
        </Typography>
        {/* <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ 
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            py: { xs: 1, sm: 1.5 }
          }}
        >
          Add Product
        </Button> */}
      </Box>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              label="Search products"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
            />
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={loadProducts}
              sx={{ minWidth: 120 }}
            >
              Search
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Name</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Barcode</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Ingredients</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {product.name}
                        </Typography>
                        {product.description && (
                          <Typography variant="caption" color="text.secondary">
                            {product.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>
                      {product.barcode}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {product.ingredients.slice(0, 2).map((ingredient, index) => (
                          <Chip
                            key={index}
                            label={ingredient}
                            size="small"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ))}
                        {product.ingredients.length > 2 && (
                          <Chip
                            label={`+${product.ingredients.length - 2}`}
                            size="small"
                            color="default"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                      {new Date(product.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteProduct(product.id)}
                          sx={{ p: 0.5 }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Product Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Adaugă Produs Nou
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Product Name"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                required
                size="small"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                value={newProduct.description}
                onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                multiline
                rows={2}
                size="small"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Barcode"
                value={newProduct.barcode}
                onChange={(e) => setNewProduct({ ...newProduct, barcode: e.target.value })}
                required
                size="small"
              />
            </Grid>
            
            {/* Ingredients */}
            <Grid size={12}>
              <Typography variant="subtitle2" gutterBottom>
                Ingredients
              </Typography>
              {newProduct.ingredients.map((ingredient, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    value={ingredient}
                    onChange={(e) => handleIngredientChange(index, e.target.value)}
                    size="small"
                    placeholder="Enter ingredient"
                  />
                  <IconButton onClick={() => removeIngredient(index)} size="small">
                    <Delete />
                  </IconButton>
                </Box>
              ))}
              <Button onClick={addIngredient} size="small" startIcon={<Add />}>
                Add Ingredient
              </Button>
            </Grid>

            {/* Nutritional Information */}
            <Grid size={12}>
              <Typography variant="subtitle2" gutterBottom>
                Nutritional Information (per 100g)
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Calories"
                type="number"
                value={newProduct.nutritional_declaration?.calories || ''}
                onChange={(e) => handleNutritionalChange('calories', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Protein (g)"
                type="number"
                value={newProduct.nutritional_declaration?.protein || ''}
                onChange={(e) => handleNutritionalChange('protein', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Carbohydrates (g)"
                type="number"
                value={newProduct.nutritional_declaration?.carbohydrates || ''}
                onChange={(e) => handleNutritionalChange('carbohydrates', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Fat (g)"
                type="number"
                value={newProduct.nutritional_declaration?.fat || ''}
                onChange={(e) => handleNutritionalChange('fat', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Fiber (g)"
                type="number"
                value={newProduct.nutritional_declaration?.fiber || ''}
                onChange={(e) => handleNutritionalChange('fiber', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Sugar (g)"
                type="number"
                value={newProduct.nutritional_declaration?.sugar || ''}
                onChange={(e) => handleNutritionalChange('sugar', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Sodium (mg)"
                type="number"
                value={newProduct.nutritional_declaration?.sodium || ''}
                onChange={(e) => handleNutritionalChange('sodium', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Saturated Fat (g)"
                type="number"
                value={newProduct.nutritional_declaration?.saturated_fat || ''}
                onChange={(e) => handleNutritionalChange('saturated_fat', e.target.value)}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveProduct} variant="contained">
            Creează
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductManagement;
